<template lang="pug">
  .registry-item
    Drawer(v-if="showDrawer" :title="__('Choose Item Destination')" @hide="showDrawer = false")
      Radio(option="cart" v-model="addTo" name="listType")
        strong {{__('Add Gifts to Current Cart')}}
        .small {{__('Combine the gifts with your current cart. Note that all gifts and personal products will ship to one address selected in checkout.')}}
      Radio(option="new" v-model="addTo" name="listType")
        .primary-default
          strong {{__('Add Gifts to A Separate Cart')}}
          .small {{__('Add the gifts to a separate order from your current cart for a registry-specific checkout experience. You can ship gifts directly to the registry address using this method.')}}
      template(#footer)
        .add-btn
          RogueButton(color="red" width="full" @click="addToCart(addTo)") {{__('Confirm Add to Selected Cart')}}
    .order-item-container
      OrderItem.reg-itm(:buyRequest="buyRequest" :list="mine ? list._id : undefined" :theme="mine ? 'my-registry' : 'registry'" :hideChangeWarning="hideChangeWarning")
        template(#after-description)
          .item-details
            strong(v-if="!mine") {{__('Quantity wanted: {count}', {count: meta.wanted || 1 })}}
            strong {{__('Quantity purchased: {count}', {count: meta.purchased || '0' })}}
            em(v-if="!mine") {{__('Priority: {priority}', {priority: meta.priority || 'Medium' })}}
        template(#stepper)
          .opts(:class="{mine: mine}")
            RogueSelect.priority(
              v-if="mine"
              label="Priority"
              v-model="meta.priority"
              @update:modelValue="updatePriority($event)"
              :options="priorities"
            )
            Field.simple-qty(label="Qty")
              BuyRequestQty(
                :buyRequest="buyRequest"
                theme="mobile"
                :listId="mine ? list._id : undefined"
              )
        template(#price)
          .price-hide
        template(#right)
          .buttons
            Price.product-price(:buyRequest="buyRequest" :showSinglePrice="true" theme="wishlist")
            RogueButton.btn(
              v-if="itemStockStatus === '' || !mine"
              :disabled="itemStockStatus !== '' || hasItemChanged"
              @click="addToCart()"
              color="red"
            ) {{__('Add to Cart')}}
            RogueButton.btn(v-else-if="itemStockStatus === 'bin'" @click="clickBinButton($el)" :loading="BIN.loading")
              bin-status-text(:bin="BIN")
            RogueButton.btn(disabled v-else) {{__('Out of Stock')}}
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useListStore } from '@/js/stores/listStore'
import { useProductStore } from '@/js/stores/productStore'
import { useBackInStock } from '@/js/components/bin/useBackInStock'
import stockStatus from '@/js/helpers/stockStatus'
import BuyRequestQty from '@/js/components/buy-request-qty.vue'
import BinStatusText from '@/js/components/bin/bin-status-text.vue'
import { productHasChanged } from '@/js/helpers/buy-request'
import { useRogue } from '@/js/services/Rogue'
import type { BuyRequest, DisplayAddress } from '@/js/services/RogueTypes'
import type { List } from '@/js/stores/listTypes'

const props = withDefaults(defineProps<{
  // Buy request
  buyRequest: BuyRequest,

  // Used to determine whether to show editing tools or not
  mine?: boolean,

  // The registry itself - for editing the list itmes
  list?: List | null,

  hideChangeWarning?: boolean,
}>(), {
  mine              : false,
  list              : null,
  hideChangeWarning : false,
})

const listStore = useListStore()
const productStore = useProductStore()
const rogue = useRogue()
const { BIN, clickBinButton } = useBackInStock(props.buyRequest)

// Dropdown values for the "Priority" select component
const priorities = ['Highest', 'High', 'Medium', 'Low', 'Lowest'].map(p => ({ label: p, value: p.toLowerCase() }))

// Drawer to pick which cart to add
const showDrawer = ref(false)

// Whether to create a new list to buy the item, or add it to the current item
const addTo = ref('cart')

const products = computed(() => productStore.products)
const cart = computed(() => listStore.cart)
const lists = computed(() => listStore.lists)

// Registry metadata
const meta = computed(() => props.buyRequest.metadata!.registry!)

// Used to determine which button to sho
const itemStockStatus = computed(() => stockStatus.forBuyRequest(props.buyRequest, products.value))

const hasItemChanged = computed(() => productHasChanged(props.buyRequest, products.value))

/**
 * Add this registry item to your cart
 * Unless provided with a list, this function will see if I already have a purchase list for this registry
 * and add the product to that list.
 * If not, it will show a sidebar and ask if they want to create a separate order or combine with their existing one
 * @param list - 'new' : create a new list, 'cart': add to current cart, undefined: check for an existing list, or prompt customer what to do
 */
async function addToCart(list?: string) {
  const buyRequest = JSON.parse(JSON.stringify(props.buyRequest)) as BuyRequest
  const registryId = props.list!._id
  // We need to update the registry once this order is purchased, so we save references to it in the metadata
  buyRequest.metadata!.registry = { registryId, itemId: props.buyRequest._id } as NonNullable<BuyRequest['metadata']>['registry']
  buyRequest._id = undefined

  const { name, shipping } = props.list!.metadata!.registry!
  await listStore.init(rogue)

  // Create a new list for this registry item
  if (list === 'new' || (list === undefined && !cart.value.items?.length)) {
    const metadata = { registry: { registryId, name, shipping } }
    // Remove the shipping ID
    const purchaseShipping = metadata.registry.shipping as DisplayAddress
    purchaseShipping._id = undefined
    // This list will be associated with the specific registry we are looking at now
    await listStore.createList(rogue, 'registryPurchase', metadata as unknown as List['metadata'])
  }

  if (list !== 'cart') {
    // Find the specified list, or if this customer already has a purchase list for this registry, or a list that has an item from this registry
    const registryBuyList = Object.values(lists.value)
      .find(l => l?.metadata?.registry?.registryId === registryId || l?.items?.some(item => item?.metadata?.registry?.registryId === registryId))

    if (registryBuyList) {
      // Switch to this as our new "Main" cart, so the checkout store will start tracking it
      await listStore.switchActiveCart(rogue, registryBuyList._id)
    } else {
      // If they have items in their current cart, we need to ask if they want to create a new cart or use their current one
      showDrawer.value = true

      // Dont add to cart yet, because it hasnt been selected
      return
    }
  }

  // If they chose a new list, it will have been created and switched to the main "cart" by now
  await listStore.addListItems(rogue, 'cart', [buyRequest])
  showDrawer.value = false
}

// Edit buy request tools - only active if the registry is my own
function updatePriority() {
  if (props.mine) {
    Object.assign(props.buyRequest.metadata!, { registry: meta.value })

    listStore.updateListItem(rogue, props.list!._id, props.buyRequest._id, { metadata: props.buyRequest.metadata })
  }
}
</script>
<style lang="scss" scoped>

.item-details {
  display: flex;
  margin-bottom: $space-xs;
  flex-direction: column;
  font-size: $size-xs;
}

.reg-itm:deep() {
  .order-item-links {
    margin-left: 0;
  }

  .qty {
    padding: 0;
    background-color: transparent;
  }

  .links > .bin-signup {
    display: none;
  }

  @include not-desktop() {
    .btns {
      margin-left: 0;
    }
  }
}

.opts {
  display: flex;
  align-items: center;
  width: 100%;

  .priority {
    flex-grow: 1;
    margin-bottom: 0;
  }
}

.mine .simple-qty {
  margin-left: $space-s;
}

.add-btn {
  padding: $space-s;
}

.buttons {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  min-width: 190px;
  margin-top: $space-s;

  .item-price {
    margin-left: 0;
  }

  .product-price {
    text-align: right;
    margin-right: $space-s;
  }

  .btn {
    flex-grow: 1;
    max-width: 170px;
  }

  @include not-desktop() {
    .price {
      text-align: left;
      min-width: 80px;
    }

    .btn {
      max-width: 100%;
    }
  }
}

.add-to-cart {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-grow: 1;

  .price {
    margin-right: $space-s;
  }

  .btn {
    width: 144px;
  }
}

@include mobile() {
  .registry-item:deep() {
    .reg-itm {

      .qty {
        padding: 0;
        background-color: transparent;
      }
    }
  }
}

</style>
